import { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./ui/Button";
import { Field } from "./ui/Field";
import { workPackageOptions } from "../lib/workPackages";
import { STATUSES, type Project, type Status, type Task, type TaskDraft } from "../types";

interface Props {
  task: Task | null;
  project: Project;
  onSave: (draft: TaskDraft, wantsDone: boolean) => void;
  onDelete: (() => void) | null;
  onClose: () => void;
}

/**
 * The status field exists here as the touch fallback. On a desktop, drag is the
 * only way to move a task between columns; HTML5 drag does not work on touch,
 * so the dialog keeps a dropdown.
 */
export function TaskDialog({ task, project, onSave, onDelete, onClose }: Props) {
  const [title, setTitle] = useState(task?.title ?? "");
  const [status, setStatus] = useState<Status>(task?.status ?? "Not Started");
  const [deadline, setDeadline] = useState(task?.deadline ?? "");
  const [workPackage, setWorkPackage] = useState(task?.workPackage ?? "");
  const [activity, setActivity] = useState(task?.activity ?? "");
  const [cooperateWith, setCooperateWith] = useState(task?.cooperateWith ?? "");
  const [resultLink, setResultLink] = useState(task?.resultLink ?? "");
  const [notes, setNotes] = useState(task?.notes ?? "");
  const [error, setError] = useState("");

  const submit = () => {
    const name = title.trim();
    if (!name) {
      setError("Give the task a name first.");
      return;
    }
    const wasDone = task?.status === "Done";
    const wantsDone = status === "Done" && !wasDone && !resultLink.trim();

    const draft: TaskDraft = {
      title: name,
      status,
      deadline,
      workPackage: workPackage.trim(),
      activity: activity.trim(),
      cooperateWith: cooperateWith.trim(),
      resultLink: resultLink.trim(),
      notes: notes.trim(),
      // Leaving Done clears the no-deliverable reason but keeps the link.
      outcome: status === "Done" ? (task?.outcome ?? "") : "",
      dateDone: task?.dateDone ?? "",
      projectId: project.id,
      projectName: project.name,
    };
    onSave(draft, wantsDone);
  };

  return (
    <Modal
      title={task ? "Edit task" : "New task"}
      subtitle={project.name}
      onClose={onClose}
      actions={
        <>
          {onDelete && (
            <Button variant="danger" onClick={onDelete}>
              Delete task
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Save changes
          </Button>
        </>
      }
    >
      <Field label="Task">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
      </Field>
      <div className="row2">
        <Field label="Status">
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Deadline">
          <input
            className="input"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </Field>
      </div>
      <div className="row2">
        <Field label="Work package">
          <select
            className="input"
            value={workPackage}
            onChange={(e) => setWorkPackage(e.target.value)}
          >
            <option value="">None</option>
            {workPackageOptions(workPackage).map((wp) => (
              <option key={wp} value={wp}>
                {wp}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Activity">
          <input
            className="input"
            placeholder="Pilot testing"
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Cooperate with">
        <input
          className="input"
          value={cooperateWith}
          onChange={(e) => setCooperateWith(e.target.value)}
        />
      </Field>
      <Field label="Result link">
        <input
          className="input"
          type="url"
          placeholder="https://"
          value={resultLink}
          onChange={(e) => setResultLink(e.target.value)}
        />
      </Field>
      <Field label="Notes">
        <textarea
          className="input"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      <div className="err">{error}</div>
    </Modal>
  );
}
